import { route } from '@/app/lib/routing'
import { resolveProductGenre } from '@/app/lib/utils'

import type { SearchProduct, SearchProductApiItem } from './types'

/**
 * `SearchProductApiItem.url` puede llegar ya con el segmento `entradas/` puesto
 * por el backend — a diferencia del resto del catálogo (`ProductApiItem.url`
 * en `mapProductToCard.ts`), donde `.url` es siempre un slug pelado sin
 * prefijo. Se retira aquí para que `route()` sea la única fuente del prefijo
 * y nunca se duplique (`/entradas/entradas/...`); si no viene, es un no-op.
 *
 * OJO: exige la barra (`entradas/`), nunca solo el guion (`entradas-`) — el
 * slug del propio evento empieza legítimamente por `entradas-` como
 * convención de nomenclatura SEO (p. ej. `entradas-el-rey-leon-el-musical`,
 * ver `mapProductToCard.test.ts`), y un regex sin la barra se lo comería.
 */
function stripEntradasPrefix(url: string): string {
  return url.replace(/^\/?entradas\//, '')
}

/**
 * `SearchProductApiItem` → `SearchProduct`: reescribe `url` para anteponer la
 * categoría (`nicename` del primer género — mismo criterio que
 * `resolveCardHref` en `mapProductToCard.ts`). Antes se usaba el `url` del
 * backend tal cual, produciendo `entradas/[slug]` sin categoría; ahora
 * `entradas/[categoria]/[slug]`.
 *
 * Sin género no hay categoría derivable: se conserva el `url` original en vez
 * de descartar el producto — mejor un enlace potencialmente incompleto (el
 * comportamiento previo) que perder la sugerencia del buscador.
 */
export function mapSearchProduct(raw: SearchProductApiItem): SearchProduct {
  const { genres, url, ...rest } = raw
  const genreSlug = resolveProductGenre(genres)?.nicename
  const slug = stripEntradasPrefix(url)

  return {
    ...rest,
    url: genreSlug ? route(`/${genreSlug}/${slug}`) : url,
    // Mismo `nicename` que arriba, conservado (no solo consumido para el
    // `url`) para la analítica de `view_item_list`
    // (`mapSearchProductToAnalyticsItem.ts`, `item_category`).
    category: genreSlug,
  }
}

/** `SearchProductApiItem[]` → `SearchProduct[]`, preservando el orden. */
export function mapMostSearchedProducts(products: SearchProductApiItem[]): SearchProduct[] {
  return products.map(mapSearchProduct)
}
